import { feedbackRead } from "components/Analytics/feedbackRead";
import React, { useEffect, useMemo, useState } from "react";
import { Line } from "react-chartjs-2";
import Select from "react-select";
import { Alert, Input, Spinner, Table } from "reactstrap";
import { FEEDBACK_REASONS } from "components/Misc/ComprehendFeedback";

// Stable color slots while cohorts remain selected. "Everyone else" stays gray and dashed.
const COHORT_COLORS = ["#2a78d6", "#eb6834", "#1baf7a"];
const BASELINE_COLOR = "#8a8f98";
// Weeks with fewer ratings than this are drawn hollow: their rate swings a lot.
const THIN_WEEK = 10;
// Before/after totals under this are called out as thin.
const THIN_PERIOD = 30;
const MARKER_COLOR = "#475467";
const DAY = 86400000;

const weekLabel = (week) =>
  new Date(`${week}T00:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric" });
const dayLabel = weekLabel;
const pct = (value) => (value == null ? "—" : `${Number(value).toFixed(1)}%`);
const pts = (value) => `${value > 0 ? "+" : value < 0 ? "−" : ""}${Math.abs(value).toFixed(1)} pts`;
const reasonLabel = (id) => FEEDBACK_REASONS.find((r) => r.id === id)?.label || id;

// The x pixel of a date: its week's point, plus how far into that week it falls.
// Clamped to the plot, since the last week's point already sits on the right edge.
function dateX(chart, weeks, date) {
  const scale = chart.scales["x-axis-0"];
  const { chartArea } = chart;
  const day = Date.parse(`${date}T00:00:00Z`);
  const first = Date.parse(`${weeks[0]}T00:00:00Z`);
  const index = Math.floor((day - first) / (7 * DAY));
  if (index < 0) return chartArea.left;
  if (index >= weeks.length) return chartArea.right;
  const here = scale.getPixelForValue(undefined, index);
  const next = index + 1 < weeks.length
    ? scale.getPixelForValue(undefined, index + 1)
    : here + (weeks.length > 1 ? here - scale.getPixelForValue(undefined, index - 1) : 0);
  const offset = (day - Date.parse(`${weeks[index]}T00:00:00Z`)) / (7 * DAY);
  return Math.min(chartArea.right, Math.max(chartArea.left, here + (next - here) * offset));
}

// "What changed?" before/after periods, shaded behind the lines.
const periodsPlugin = {
  beforeDatasetsDraw(chart) {
    const { periods, weeks } = chart.options.feedbackPeriods || {};
    if (!periods || !weeks?.length) return;
    const { ctx, chartArea } = chart;
    periods.forEach((period) => {
      const left = dateX(chart, weeks, period.startDate);
      const right = dateX(chart, weeks, period.endAfter);
      if (right <= left) return;
      ctx.save();
      ctx.fillStyle = period.color;
      ctx.fillRect(left, chartArea.top, right - left, chartArea.bottom - chartArea.top);
      ctx.fillStyle = "#475467";
      ctx.font = "600 10px Inter, sans-serif";
      ctx.textAlign = "left";
      ctx.fillText(period.label, left + 4, chartArea.bottom - 6);
      ctx.restore();
    });
  },
};

// Chart notes as numbered dashed lines on their date.
const notesPlugin = {
  afterDatasetsDraw(chart) {
    const { notes, weeks } = chart.options.feedbackNotes || {};
    if (!notes?.length || !weeks?.length) return;
    const { ctx, chartArea } = chart;
    notes.forEach((note) => {
      const x = dateX(chart, weeks, note.date);
      ctx.save();
      ctx.strokeStyle = MARKER_COLOR;
      ctx.setLineDash([4, 4]);
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(x, chartArea.top + 10);
      ctx.lineTo(x, chartArea.bottom);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillStyle = MARKER_COLOR;
      ctx.beginPath();
      ctx.arc(x, chartArea.top + 2, 8, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = "#ffffff";
      ctx.font = "bold 10px Inter, sans-serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(String(note.number), x, chartArea.top + 2);
      ctx.restore();
    });
  },
};

// A vertical line that follows the cursor, snapped to the hovered week (the
// tooltip already reads every line at that week).
const crosshairPlugin = {
  afterDatasetsDraw(chart) {
    const active = chart.tooltip && chart.tooltip._active;
    if (!active || !active.length) return;
    const x = active[0]._view.x;
    const { ctx, chartArea } = chart;
    ctx.save();
    ctx.strokeStyle = "#98a2b3";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(x, chartArea.top);
    ctx.lineTo(x, chartArea.bottom);
    ctx.stroke();
    ctx.restore();
  },
};

// One weekly chart for everything in section 03: the measure (overall thumbs-down
// rate, or a reason, optionally in a section), the groups (everyone, or up to three
// cohorts, optionally vs everyone else), chart notes, and an optional before/after
// read around a change. A cohort's membership is fixed, so its line follows the
// same people across the whole range.
export default function FeedbackTrends({ apiClient, request, cohorts, annotations, sectionOptions = [] }) {
  const [slots, setSlots] = useState({}); // cohortId -> color slot, kept while selected
  const [everyoneElse, setEveryoneElse] = useState(true);
  const [reason, setReason] = useState("");
  const [section, setSection] = useState("");
  const [showTable, setShowTable] = useState(false);
  const [yMax, setYMax] = useState(""); // "" = automatic
  const [trend, setTrend] = useState(null);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);
  const [comparing, setComparing] = useState(false);
  const [changeNote, setChangeNote] = useState("");
  const [changeDate, setChangeDate] = useState("");
  const [weeksAround, setWeeksAround] = useState(3);
  const [pair, setPair] = useState([]);
  const [compare, setCompare] = useState(null);
  const [compareError, setCompareError] = useState("");

  // A removed cohort drops out of the comparison.
  useEffect(() => {
    setSlots((current) => {
      const kept = Object.fromEntries(Object.entries(current).filter(([id]) => cohorts.some((c) => c.cohortId === id)));
      return Object.keys(kept).length === Object.keys(current).length ? current : kept;
    });
  }, [cohorts]);

  const cohortIds = useMemo(() => Object.keys(slots).sort((a, b) => slots[a] - slots[b]), [slots]);
  const measure = useMemo(() => ({ reason: reason || undefined, section: (reason && section) || undefined }), [reason, section]);
  const body = useMemo(() => ({
    ...request, cohortIds, everyoneElse,
    measureReason: measure.reason, measureSection: measure.section,
  }), [request, cohortIds, everyoneElse, measure]);

  useEffect(() => {
    let active = true;
    setError("");
    setTrend(null);
    feedbackRead(apiClient, "/v1/notes/feedback/trend", body, () => active).then((response) => {
      if (!active) return;
      if (response?.error) throw new Error(response.error);
      setTrend(response);
    }).catch(() => {
      if (active) setError("Unable to load the weekly trend. Try again.");
    });
    return () => { active = false; };
  }, [apiClient, body, retry]);

  const series = trend?.series || [];
  const seriesKeys = series.map((line) => line.key).join(",");
  // Default the before/after pair to the first two lines shown.
  useEffect(() => {
    const keys = seriesKeys ? seriesKeys.split(",") : [];
    setPair((current) => {
      const kept = current.filter((key) => keys.includes(key));
      const filled = [...kept, ...keys.filter((key) => !kept.includes(key))].slice(0, Math.min(2, keys.length));
      return filled.join(",") === current.join(",") ? current : filled;
    });
  }, [seriesKeys]);

  // With nothing in the events log, there is nothing to pick: go straight to a date.
  const pickDate = changeNote === "custom" || !annotations.length;
  const noteDate = annotations.find((note) => note.annotationId === changeNote)?.date;
  const change = pickDate ? changeDate : noteDate;

  useEffect(() => {
    setCompare(null);
    setCompareError("");
    if (!comparing || !change || !pair.length || !trend) return undefined;
    let active = true;
    feedbackRead(apiClient, "/v1/notes/feedback/trend/compare", {
      ...body, changeDate: change, weeks: weeksAround, groups: pair,
    }, () => active).then((response) => {
      if (!active) return;
      if (response?.error) throw new Error(response.error);
      setCompare(response);
    }).catch(() => {
      if (active) setCompareError("Unable to compare before and after. Try again.");
    });
    return () => { active = false; };
  }, [apiClient, body, trend, comparing, change, weeksAround, pair, retry]);

  const choose = (options) => {
    const ids = (options || []).map((option) => option.value);
    setSlots((current) => {
      const next = {};
      ids.forEach((id) => { if (id in current) next[id] = current[id]; });
      ids.forEach((id) => {
        if (!(id in next)) {
          const used = Object.values(next);
          let slot = 0;
          while (used.includes(slot)) slot += 1;
          next[id] = slot;
        }
      });
      return next;
    });
  };

  const cohortOptions = cohorts.map((c) => ({ value: c.cohortId, label: `${c.name} (${c.userIds.length})` }));
  const colorOf = (line) => {
    if (line.key === "everyoneElse") return BASELINE_COLOR;
    if (line.key === "everyone") return COHORT_COLORS[0];
    const slot = slots[line.key];
    return COHORT_COLORS[slot] || `hsl(${(slot * 137.508) % 360}, 60%, 42%)`;
  };

  const weeks = trend?.weeks || [];
  const firstDay = weeks[0] || "";
  const lastDay = weeks.length
    ? new Date(Date.parse(`${weeks[weeks.length - 1]}T00:00:00Z`) + 6 * DAY).toISOString().slice(0, 10)
    : "";
  // Weeks are Monday to Sunday, so the first and last can hold only part of a week.
  const partial = (index) => (index === 0 && firstDay < request.startDate)
    || (index === weeks.length - 1 && lastDay > request.endDate);
  const notes = annotations
    .filter((note) => weeks.length && note.date >= request.startDate && note.date <= request.endDate
      && note.date >= firstDay && note.date <= lastDay)
    .sort((a, b) => (a.date < b.date ? -1 : 1))
    .map((note, index) => ({
      ...note, number: index + 1,
      cohortName: cohorts.find((c) => c.cohortId === note.cohortId)?.name,
    }));

  const measureName = reason
    ? `Ratings flagging “${reasonLabel(reason)}”${section ? ` in ${section}` : ""}`
    : "Thumbs-down rate";
  const data = {
    labels: weeks.map(weekLabel),
    datasets: series.map((line) => {
      const color = colorOf(line);
      return {
        key: line.key,
        label: line.label,
        data: line.points.map((point) => point.rate),
        borderColor: color,
        backgroundColor: color,
        borderWidth: 2,
        borderDash: line.key === "everyoneElse" ? [6, 4] : [],
        pointRadius: 4,
        pointHoverRadius: 6,
        pointBorderColor: color,
        pointBackgroundColor: line.points.map((point, index) => (
          point.rated < THIN_WEEK || partial(index) ? "#ffffff" : color)),
        fill: false,
        lineTension: 0.2,
        spanGaps: false,
      };
    }),
  };
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: { top: 14 } },
    feedbackNotes: { notes, weeks },
    feedbackPeriods: compare ? {
      weeks,
      periods: [
        { label: "Before", color: "rgba(152, 162, 179, 0.14)", startDate: compare.before.startDate,
          endAfter: compare.changeDate },
        { label: "After", color: "rgba(13, 148, 136, 0.10)", startDate: compare.changeDate,
          endAfter: new Date(Date.parse(`${compare.after.endDate}T00:00:00Z`) + DAY).toISOString().slice(0, 10) },
      ],
    } : null,
    hover: { mode: "index", intersect: false },
    legend: {
      display: series.length > 1,
      position: "bottom",
      labels: { boxWidth: 8, usePointStyle: true, padding: 20, fontColor: "#4b5563" },
    },
    tooltips: {
      mode: "index",
      intersect: false,
      backgroundColor: "#111827",
      titleFontColor: "#ffffff",
      bodyFontColor: "#ffffff",
      cornerRadius: 6,
      xPadding: 12,
      yPadding: 10,
      callbacks: {
        title: (items) => (items.length
          ? `Week of ${items[0].label}${partial(items[0].index) ? " (part of the week)" : ""}`
          : ""),
        label: (item) => {
          const point = series[item.datasetIndex].points[item.index];
          return `${series[item.datasetIndex].label}: ${pct(point.rate)} (${point.hits} of ${point.rated})`;
        },
      },
    },
    scales: {
      xAxes: [{ gridLines: { display: false }, ticks: { fontColor: "#7c8799" } }],
      yAxes: [{
        gridLines: { color: "#edf0f5", drawBorder: false },
        ticks: {
          beginAtZero: true,
          ...(yMax ? { max: Number(yMax) } : { suggestedMax: reason ? 10 : 40 }),
          fontColor: "#7c8799",
          callback: (value) => `${value}%`,
        },
      }],
    },
  };
  const rated = series.some((line) => line.points.some((point) => point.rated));

  return (
    <div className="note-intel-panel fb-panel">
      <div className="note-intel-panel-header">
        <div>
          <h3>{measureName} by week</h3>
          <p>
            {reason
              ? `Out of every 100 rated notes (thumbs up or down), how many got a thumbs down that flagged this${section ? " in this section" : ""}. Lower is better.`
              : "Out of every 100 rated notes, how many got a thumbs down. Lower is better."}
            {" "}Numbered lines are notes from the events log.
          </p>
        </div>
      </div>

      <div className="fb-trend-controls">
        <div className="fb-measure">
          <Input type="select" aria-label="Measure" value={reason}
            onChange={(event) => { setReason(event.target.value); if (!event.target.value) setSection(""); }}>
            <option value="">Overall thumbs-down rate</option>
            {FEEDBACK_REASONS.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
          </Input>
          {reason && <Input type="select" aria-label="In section" value={section} onChange={(event) => setSection(event.target.value)}>
            <option value="">In any section</option>
            {sectionOptions.map((name) => <option key={name} value={name}>In {name}</option>)}
          </Input>}
        </div>
        <div className="fb-trend-cohorts">
          <Select inputId="fb-trend-cohorts" classNamePrefix="note-intel-select" isMulti
            placeholder={cohorts.length ? "Everyone · select cohorts to compare" : "Everyone"}
            options={cohortOptions}
            value={cohortIds.map((id) => cohortOptions.find((option) => option.value === id)).filter(Boolean)}
            onChange={choose} aria-label="Cohorts to compare" />
        </div>
        <label className={`fb-inline-check${cohortIds.length ? "" : " fb-disabled"}`}>
          <input type="checkbox" checked={everyoneElse} disabled={!cohortIds.length}
            onChange={(event) => setEveryoneElse(event.target.checked)} />
          vs everyone else
        </label>
        {!showTable && (
          <label className="fb-ymax" title="Top of the y-axis. Leave blank for automatic.">
            <span>Y max</span>
            <input type="number" inputMode="numeric" min={1} max={100} step={1} placeholder="Auto" value={yMax}
              aria-label="Y-axis maximum percent (blank for automatic)"
              onChange={(event) => setYMax(event.target.value)}
              onBlur={() => setYMax((value) => (value === "" ? "" : String(Math.min(100, Math.max(1, Math.round(Number(value)) || 1)))))}
              onKeyDown={(event) => { if (event.key === "Escape") setYMax(""); }} />
            <span aria-hidden="true">%</span>
          </label>
        )}
        <button type="button" className="fb-show-all" onClick={() => setShowTable(!showTable)}>
          {showTable ? "Show chart" : "Show table"}
        </button>
      </div>

      {error && <Alert color="danger">{error} <button type="button" onClick={() => setRetry(retry + 1)}>Retry</button></Alert>}
      {!trend && !error && <div className="fb-loading"><Spinner size="sm" /> Loading the weekly trend…</div>}
      {trend && !rated && <p className="fb-muted">No ratings in these filters yet.</p>}
      {trend && rated && (showTable
        ? <div className="table-responsive"><Table size="sm" className="fb-trend-table">
            <thead><tr><th>Week of</th>{series.map((line) => <th key={line.key}>{line.label}</th>)}</tr></thead>
            <tbody>{weeks.map((week, index) => (
              <tr key={week}>
                <td>{weekLabel(week)}</td>
                {series.map((line) => {
                  const point = line.points[index];
                  return <td key={line.key}>{pct(point.rate)} <span className="fb-muted">({point.hits} of {point.rated})</span></td>;
                })}
              </tr>
            ))}</tbody>
          </Table></div>
        : <div className="fb-trend-chart">
            <Line data={data} options={options} plugins={[periodsPlugin, notesPlugin, crosshairPlugin]}
              datasetKeyProvider={(dataset) => dataset.key} />
          </div>)}

      {trend && rated && <p className="fb-footnote">
        Hover for counts. Hollow points are weeks with fewer than {THIN_WEEK} ratings, or only part of a week in range.
        {cohortIds.length > 0 && everyoneElse && " \"Everyone else\" is everyone not in the selected cohorts."}
      </p>}

      {!!notes.length && <ol className="fb-notes-legend">
        {notes.map((note) => (
          <li key={note.annotationId}>
            <span className="fb-note-number">{note.number}</span>
            <span>{note.label}</span>
            <span className="fb-muted">{dayLabel(note.date)}{note.cohortName ? ` · ${note.cohortName}` : ""}</span>
          </li>
        ))}
      </ol>}

      <div className="fb-what-changed">
        <button type="button" className="fb-show-all" aria-expanded={comparing} onClick={() => setComparing(!comparing)}>
          {comparing ? "Hide what changed" : "What changed? Compare before and after a change"}
        </button>
        {comparing && <>
          <div className="fb-compare-controls">
            {!!annotations.length && <Input type="select" aria-label="Change" value={changeNote} onChange={(event) => setChangeNote(event.target.value)}>
              <option value="">Pick a change…</option>
              {annotations.slice().sort((a, b) => (a.date < b.date ? -1 : 1)).map((note) => (
                <option key={note.annotationId} value={note.annotationId}>{dayLabel(note.date)} · {note.label}</option>
              ))}
              <option value="custom">Enter a date…</option>
            </Input>}
            {pickDate && <Input type="date" aria-label="Change date" value={changeDate}
              onChange={(event) => setChangeDate(event.target.value)} />}
            <Input type="select" aria-label="Weeks on each side" value={weeksAround}
              onChange={(event) => setWeeksAround(Number(event.target.value))}>
              {[2, 3, 4].map((n) => <option key={n} value={n}>{n} weeks before and after</option>)}
            </Input>
            {series.length > 2 && [0, 1].map((slot) => (
              <Input key={slot} type="select" aria-label={`Group ${slot + 1}`} value={pair[slot] || ""}
                onChange={(event) => setPair((current) => {
                  const next = [...current];
                  next[slot] = event.target.value;
                  return next;
                })}>
                {series.map((line) => (
                  <option key={line.key} value={line.key} disabled={pair[1 - slot] === line.key}>{line.label}</option>
                ))}
              </Input>
            ))}
          </div>
          {compareError && <Alert color="danger">{compareError} <button type="button" onClick={() => setRetry(retry + 1)}>Retry</button></Alert>}
          {!change && <p className="fb-muted">
            {annotations.length
              ? "Pick a note from the events log, or enter the date a change started."
              : "Enter the date a change started. Notes you add to the events log below also show up here."}
          </p>}
          {compare && <div className="fb-compare-results">
            <p className="fb-comparison-title">{measureName} <span>Lower is better · periods shaded on the chart</span></p>
            <div className="fb-comparison-scroll">
              <table className="fb-comparison-table">
                <thead><tr>
                  <th scope="col">Group</th>
                  <th scope="col">Before <small>{dayLabel(compare.before.startDate)} – {dayLabel(compare.before.endDate)}</small></th>
                  <th scope="col">After <small>{dayLabel(compare.after.startDate)} – {dayLabel(compare.after.endDate)}</small></th>
                  <th scope="col">Change</th>
                </tr></thead>
                <tbody>{compare.groups.map((group) => {
                  const thin = group.before.rated < THIN_PERIOD || group.after.rated < THIN_PERIOD;
                  return <tr key={group.key}>
                    <th scope="row">{group.label}{thin && <small className="fb-small-group">Fewer than {THIN_PERIOD} ratings in a period</small>}</th>
                    <td><strong>{pct(group.before.rate)}</strong><small>{group.before.hits} of {group.before.rated} rated</small></td>
                    <td><strong>{pct(group.after.rate)}</strong><small>{group.after.hits} of {group.after.rated} rated</small></td>
                    <td><strong className={group.change < 0 ? "fb-good" : group.change > 0 ? "fb-bad" : ""}>{group.change == null ? "—" : pts(group.change)}</strong>
                      {group.change == null && <small>Needs ratings in both periods</small>}</td>
                  </tr>;
                })}</tbody>
              </table>
            </div>
          </div>}
        </>}
      </div>
    </div>
  );
}
